import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, X } from 'lucide-react';
import { ROUTES } from 'config';
import { useAuth } from 'contexts/AuthContext';
import { validateEmail } from 'utils/HelperFn';
import LazyImage from 'components/ui/LazyImage';

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
}

interface LoginForm {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const Login: React.FC<Props> = ({ onSuccess, onCancel, isModal = false }) => {
  const { login, error: authError, clearError } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Track previous pathname to detect navigation away from login
  const prevPathnameRef = useRef<string>(location.pathname);

  // Clear errors and countdown when navigating away from login/forgot password pages
  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    const currentPath = location.pathname;

    // If navigating away from login/forgot password pages, clear everything
    if (
      (prevPath === ROUTES.LOGIN || prevPath === ROUTES.FORGOT_PASSWORD) &&
      currentPath !== ROUTES.LOGIN &&
      currentPath !== ROUTES.FORGOT_PASSWORD
    ) {
      setErrors({});
      clearError();
    }

    // If navigating TO login page from another page (not from forgot password), clear errors
    if (
      currentPath === ROUTES.LOGIN &&
      prevPath !== ROUTES.LOGIN &&
      prevPath !== ROUTES.FORGOT_PASSWORD
    ) {
      setErrors({});
      clearError();
    }

    prevPathnameRef.current = currentPath;
  }, [location.pathname, clearError]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    // Clear previous errors but keep authError for detection
    setErrors({});
    clearError(); // Clear authError from context

    try {
      await login(formData.email, formData.password, rememberMe);

      // Call success callback
      onSuccess?.();

      // Reset form and clear any errors on success
      setFormData({ email: '', password: '' });
      setRememberMe(false);
      setErrors({});
      clearError();
    } catch (error) {
      // Error is already handled by AuthContext, but we can show it in the form
      const errorMessage = error instanceof Error ? error.message : 'Invalid email or password. Please try again.';
      setErrors({
        general: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isModal && (
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>
      )}
      <div className={isModal ? "p-6" : "min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8"}>
        <div className={isModal ? "w-full" : "max-w-md w-full space-y-8"}>
          <div className="text-center mb-6">
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-32">
                <LazyImage
                  src="/images/logo.png"
                  alt="Abbey Dental Supplies"
                  className="w-full h-full object-cover"
                  sizes="128px"
                />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Welcome to Abbey Dental Supplies
            </h2>
          </div>

          {/* Login Form */}
          <div className={isModal ? "w-full" : "bg-white rounded-2xl shadow-xl p-8"}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {(errors.general || authError) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{errors.general || authError}</p>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`block w-full pl-10 pr-12 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 h-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 h-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  onClick={() => {
                    if (isModal && onCancel) {
                      onCancel();
                    }
                  }}
                  className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Sign in
                    <ArrowRight className="ml-2 h-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">New to Abbey Dental Supplies?</span>
                </div>
              </div>
            </div>

            {/* Sign Up Link */}
            <div className="mt-6 text-center">
              <Link 
                to={ROUTES.REGISTER} 
                onClick={() => {
                  if (isModal && onCancel) {
                    onCancel();
                  }
                }}
                className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                Create an account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
